/**
 * /admin/mandanten — die vier Handgriffe, die nur der Plattform-Administrator hat (spec §1.11):
 * einen Auftraggeber anlegen, den ersten Owner einladen, einen bestehenden Auftragnehmer verknüpfen und
 * ein Steuerprofil freigeben.
 *
 * Das Webhook-Secret der Neuanlage steht genau in dieser einen Antwort; danach kann es nur noch ersetzt,
 * nie gelesen werden (routes/admin.ts, POST /api/admin/tenants). Deshalb steht es hier mit beiden
 * fertigen URLs und einer Warnung — und verschwindet, sobald es verborgen wird.
 *
 * Die Freigabe eines Steuerprofils ist plattformweit (routes/tenant.ts, `requirePlatformAdmin`), liest
 * die Empfänger aber über `GET /api/t/:id/team` — und dieser Weg verlangt zusätzlich eine
 * Mitgliedschaft. Für Auftraggeber, bei denen die Sitzung kein Mitglied ist, sagt die Seite genau das,
 * statt eine leere Liste zu zeigen.
 *
 * Gerechnet wird hier nichts. Geprüft wird vorab mit denselben zod-Schemata, mit denen der Worker den
 * Körper prüft; entschieden wird trotzdem am Server, und seine Antwort steht wörtlich da.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState, type ReactElement } from 'react'
import {
  createTenantSchema, inviteOwnerSchema, linkContractorSchema,
} from '../../../shared/schemas/tenant'
import { api, type AdminTenantZeile, type TeamZeile, type WebhookGeheimnis } from '../../api'
import { RequireSession, useSitzung } from '../../auth'
import { Feld } from '../../components/Feld'
import { Knopf } from '../../components/Knopf'
import { SteuerprofilFreigabeDialog } from '../../components/SteuerprofilDialog'
import { Zustand, fehlermeldung } from '../../components/Zustand'
import { datumZeit } from '../../format'
import { STEUERPROFIL_TEXT } from '../../rechnungenModell'

/** Die Antwort von POST /api/admin/tenants: der Auftraggeber plus das einmalige Geheimnis. */
type Neuanlage = WebhookGeheimnis & { tenant: { slug: string } }

type TextFeld =
  | 'slug' | 'name' | 'legal_name' | 'contact_email' | 'billing_email' | 'reply_to_email' | 'vat_id'

const NEU_FELDER: ReadonlyArray<[TextFeld, string]> = [
  ['slug', 'Kürzel (slug)'],
  ['name', 'Anzeigename'],
  ['legal_name', 'Firmierung'],
  ['contact_email', 'Kontakt-E-Mail'],
  ['billing_email', 'Rechnungskopie (CC)'],
  ['reply_to_email', 'Antwortadresse'],
  ['vat_id', 'USt-IdNr. (optional)'],
]

type AdressFeld = 'street' | 'zip' | 'city' | 'country'

const ADRESS_FELDER: ReadonlyArray<[AdressFeld, string]> = [
  ['street', 'Straße'], ['zip', 'PLZ'], ['city', 'Ort'], ['country', 'Land (zwei Buchstaben)'],
]

/**
 * Die erste zod-Meldung, wörtlich, zusammen mit dem Feld, das sie meint — wie in `einstellungen.tsx`.
 * Der Schlüssel ist der zusammengesetzte Pfad (`address.street`), damit die Meldung an dem Feld steht,
 * das sie beanstandet, statt in einem Absatz weit darunter ohne Bezug zur Eingabe.
 */
interface Eingabefehler {
  feld: string | null
  meldung: string
}

interface ZodMeldung { readonly path: readonly PropertyKey[]; readonly message: string }

function ersterFehler(fehler: { issues: readonly ZodMeldung[] }): Eingabefehler {
  const erste = fehler.issues[0]
  if (erste === undefined) return { feld: null, meldung: 'Die Eingabe ist ungültig.' }
  const pfad = erste.path.filter((teil): teil is string => typeof teil === 'string').join('.')
  return { feld: pfad === '' ? null : pfad, meldung: erste.message }
}

/** Die Meldung für genau dieses Feld — oder `undefined`, damit `Feld` es nicht als ungültig markiert. */
function meldungFuer(fehler: Eingabefehler | null, feld: string): string | undefined {
  return fehler !== null && fehler.feld === feld ? fehler.meldung : undefined
}

/**
 * `useSitzung()` wirft, solange die Sitzungsabfrage noch keine Daten hat — also auch beim ersten
 * Aufbau der Seite, nicht nur ohne Anmeldung. Der Zugangsschutz steht deshalb an der Route, nicht im
 * Bildschirm: erst wenn <RequireSession> die Sitzung hat, wird der Bildschirm überhaupt gerendert.
 */
function MandantenRoute(): ReactElement {
  return (
    <RequireSession>
      <MandantenScreen />
    </RequireSession>
  )
}

export const Route = createFileRoute('/admin/mandanten')({ component: MandantenRoute })

export function MandantenScreen(): ReactElement {
  const sitzung = useSitzung()
  const mandanten = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => api.get<{ tenants: AdminTenantZeile[] }>('/api/admin/tenants'),
  })

  return (
    <div className="seite">
      <h1>Auftraggeber</h1>

      <Neuanlegen />

      <section aria-label="Bestehende Auftraggeber">
        <h2>Bestehende Auftraggeber</h2>
        {mandanten.isPending && <Zustand art="laedt" beschriftung="Die Auftraggeber werden geladen." />}
        {mandanten.isError && (
          <Zustand
            art="fehler"
            fehler={mandanten.error}
            onWiederholen={() => { void mandanten.refetch() }}
          />
        )}
        {mandanten.data?.tenants.length === 0 && (
          <Zustand
            art="leer"
            titel="Noch kein Auftraggeber angelegt"
            hinweis="Legen Sie den ersten Auftraggeber an. Sie erhalten dabei das Webhook-Geheimnis, das genau einmal angezeigt wird."
          />
        )}
        {(mandanten.data?.tenants ?? []).map((mandant) => (
          <MandantKarte
            key={mandant.id}
            mandant={mandant}
            istMitglied={sitzung.tenants.some((eintrag) => eintrag.id === mandant.id)}
          />
        ))}
      </section>
    </div>
  )
}

// -----------------------------------------------------------------------------------------------------
// Neuanlage samt einmaligem Geheimnis
// -----------------------------------------------------------------------------------------------------

function Neuanlegen(): ReactElement {
  const queryClient = useQueryClient()
  const [entwurf, setzeEntwurf] = useState<Record<string, string>>({})
  const [pruefFehler, setzePruefFehler] = useState<Eingabefehler | null>(null)
  const [angelegt, setzeAngelegt] = useState<Neuanlage | null>(null)

  const anlegen = useMutation({
    mutationFn: (rumpf: unknown) => api.post<Neuanlage>('/api/admin/tenants', rumpf),
    onSuccess: (antwort) => {
      setzeAngelegt(antwort)
      setzeEntwurf({})
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
  })

  const absenden = (): void => {
    // Ein leeres Pflichtfeld beanstandet das Schema; die USt-IdNr. ist als einziges Feld optional und
    // geht leer als `null` hinaus, nicht als leerer Text.
    const rumpf = {
      slug: entwurf.slug ?? '',
      name: entwurf.name ?? '',
      legal_name: entwurf.legal_name ?? '',
      contact_email: entwurf.contact_email ?? '',
      billing_email: entwurf.billing_email ?? '',
      reply_to_email: entwurf.reply_to_email ?? '',
      vat_id: (entwurf.vat_id ?? '') === '' ? null : entwurf.vat_id,
      address: {
        street: entwurf['address.street'] ?? '',
        zip: entwurf['address.zip'] ?? '',
        city: entwurf['address.city'] ?? '',
        country: entwurf['address.country'] ?? '',
      },
    }
    const geprueft = createTenantSchema.safeParse(rumpf)
    if (!geprueft.success) {
      anlegen.reset()
      setzePruefFehler(ersterFehler(geprueft.error))
      return
    }
    setzePruefFehler(null)
    anlegen.mutate(geprueft.data)
  }

  return (
    <section aria-label="Neuen Auftraggeber anlegen">
      <h2>Neu anlegen</h2>
      <div className="detail">
        {NEU_FELDER.map(([name, label]) => (
          <Feld key={name} label={label} htmlFor={`neu-${name}`} fehler={meldungFuer(pruefFehler, name)}>
            <input
              id={`neu-${name}`}
              type="text"
              value={entwurf[name] ?? ''}
              onChange={(ereignis) => { setzeEntwurf({ ...entwurf, [name]: ereignis.target.value }) }}
            />
          </Feld>
        ))}
        {ADRESS_FELDER.map(([name, label]) => (
          <Feld
            key={name}
            label={label}
            htmlFor={`neu-adresse-${name}`}
            fehler={meldungFuer(pruefFehler, `address.${name}`)}
          >
            <input
              id={`neu-adresse-${name}`}
              type="text"
              value={entwurf[`address.${name}`] ?? ''}
              onChange={(ereignis) => {
                setzeEntwurf({ ...entwurf, [`address.${name}`]: ereignis.target.value })
              }}
            />
          </Feld>
        ))}
      </div>
      <p className="knoepfe">
        <Knopf disabled={anlegen.isPending} onClick={absenden}>Auftraggeber anlegen</Knopf>
      </p>
      {pruefFehler !== null && pruefFehler.feld === null && (
        <p role="alert" className="fehler">{pruefFehler.meldung}</p>
      )}
      {anlegen.isError && <p role="alert" className="fehler">{fehlermeldung(anlegen.error)}</p>}

      {angelegt !== null && (
        <div className="detail" role="group" aria-label="Einmaliges Webhook-Secret">
          <p>Angelegt: {angelegt.tenant.slug}</p>
          <p className="warnung">
            Dieses Secret wird nie wieder angezeigt. Bitte jetzt in beide FinSuites-Workflows übernehmen.
          </p>
          <p className="mono">{angelegt.webhook_secret}</p>
          <p className="mono">{angelegt.hook_urls.deal}</p>
          <p className="mono">{angelegt.hook_urls.payment}</p>
          <Knopf
            variante="still"
            onClick={() => {
              // Auch die Antwort der Mutation wird weggeworfen: bliebe sie im Cache von TanStack
              // Query stehen, wäre das Geheimnis nach dem Verbergen weiter lesbar — und genau das
              // sagt der Satz darüber nicht.
              setzeAngelegt(null)
              anlegen.reset()
            }}
          >
            Secret verbergen
          </Knopf>
        </div>
      )}
    </section>
  )
}

// -----------------------------------------------------------------------------------------------------
// Ein bestehender Auftraggeber: Einladung, Verknüpfung, Steuerprofile
// -----------------------------------------------------------------------------------------------------

function MandantKarte({ mandant, istMitglied }: {
  mandant: AdminTenantZeile
  istMitglied: boolean
}): ReactElement {
  const tid = encodeURIComponent(mandant.id)
  const [mail, setzeMail] = useState('')
  const [rolle, setzeRolle] = useState<'owner' | 'admin'>('owner')
  const [auftragnehmerId, setzeAuftragnehmerId] = useState('')
  const [pruefFehler, setzePruefFehler] = useState<Eingabefehler | null>(null)

  const einladen = useMutation({
    mutationFn: (rumpf: { email: string; role: 'owner' | 'admin' }) =>
      api.post<{ einladung_id: string; gesendet_an: string; rolle: string }>(
        `/api/admin/tenants/${tid}/invite-owner`, rumpf,
      ),
  })
  const verknuepfen = useMutation({
    mutationFn: (rumpf: { contractor_id: string }) =>
      api.post<{ tenant_contractor_id: string; neu: boolean }>(
        `/api/admin/tenants/${tid}/link-contractor`, rumpf,
      ),
  })

  return (
    <article>
      <h3>{mandant.name}</h3>
      <p className="mono">
        {mandant.slug} · {mandant.legal_name} · angelegt {datumZeit(mandant.created_at)}
      </p>

      <div className="detail">
        <Feld
          label="E-Mail des Owners"
          htmlFor={`owner-${mandant.id}`}
          fehler={meldungFuer(pruefFehler, 'email')}
          hinweis="Die Einladung ist der einzige Weg, wie jemand außer dem Plattform-Administrator diesen Auftraggeber öffnen kann."
        >
          <input
            id={`owner-${mandant.id}`}
            type="email"
            value={mail}
            onChange={(ereignis) => { setzeMail(ereignis.target.value) }}
          />
        </Feld>
        <Feld label="Rolle" htmlFor={`rolle-${mandant.id}`}>
          <select
            id={`rolle-${mandant.id}`}
            value={rolle}
            onChange={(ereignis) => { setzeRolle(ereignis.target.value === 'admin' ? 'admin' : 'owner') }}
          >
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
          </select>
        </Feld>
      </div>
      <p className="knoepfe">
        <Knopf
          disabled={einladen.isPending}
          onClick={() => {
            const geprueft = inviteOwnerSchema.safeParse({ email: mail, role: rolle })
            if (!geprueft.success) {
              einladen.reset()
              setzePruefFehler(ersterFehler(geprueft.error))
              return
            }
            setzePruefFehler(null)
            einladen.mutate({ email: geprueft.data.email, role: rolle })
          }}
        >
          Owner einladen
        </Knopf>
      </p>
      {einladen.isSuccess && (
        <p className="meldung">
          Die Einladung ging an {einladen.data.gesendet_an} ({einladen.data.rolle}).
        </p>
      )}
      {einladen.isError && <p role="alert" className="fehler">{fehlermeldung(einladen.error)}</p>}

      <div className="detail">
        <Feld
          label="Auftragnehmer-ID"
          htmlFor={`ctr-${mandant.id}`}
          fehler={meldungFuer(pruefFehler, 'contractor_id')}
          hinweis="Der Weg ohne Einladung: die Person hat bereits einen Zugang und ein Auftragnehmer-Profil."
        >
          <input
            id={`ctr-${mandant.id}`}
            type="text"
            value={auftragnehmerId}
            onChange={(ereignis) => { setzeAuftragnehmerId(ereignis.target.value) }}
          />
        </Feld>
      </div>
      <p className="knoepfe">
        <Knopf
          disabled={verknuepfen.isPending}
          onClick={() => {
            const geprueft = linkContractorSchema.safeParse({ contractor_id: auftragnehmerId })
            if (!geprueft.success) {
              verknuepfen.reset()
              setzePruefFehler(ersterFehler(geprueft.error))
              return
            }
            setzePruefFehler(null)
            verknuepfen.mutate({ contractor_id: geprueft.data.contractor_id })
          }}
        >
          Auftragnehmer verknüpfen
        </Knopf>
      </p>
      {verknuepfen.isSuccess && (
        <p className="meldung">
          {verknuepfen.data.neu
            ? `Verknüpft als ${verknuepfen.data.tenant_contractor_id}.`
            : `Diese Verknüpfung bestand bereits (${verknuepfen.data.tenant_contractor_id}).`}
        </p>
      )}
      {verknuepfen.isError && <p role="alert" className="fehler">{fehlermeldung(verknuepfen.error)}</p>}

      {istMitglied
        ? <Steuerprofile tenantId={mandant.id} />
        : (
          <p className="hinweis">
            Für diesen Auftraggeber liegt keine Mitgliedschaft vor. Die Empfängerliste ist nur für
            Mitglieder lesbar; ohne sie lässt sich hier kein Steuerprofil freigeben.
          </p>
        )}
    </article>
  )
}

// -----------------------------------------------------------------------------------------------------
// Steuerprofile eines Auftraggebers
// -----------------------------------------------------------------------------------------------------

function Steuerprofile({ tenantId }: { tenantId: string }): ReactElement {
  const tid = encodeURIComponent(tenantId)
  const queryClient = useQueryClient()

  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tid}/team`),
  })
  /** Die Zeile, deren Steuerprofil gerade zur Freigabe nachgefragt wird — `null`: kein Dialog. */
  const [freigabe, setzeFreigabe] = useState<TeamZeile | null>(null)
  const freigeben = useMutation({
    mutationFn: (tcId: string) =>
      api.post<{ mitglied: TeamZeile }>(`/api/t/${tid}/team/${encodeURIComponent(tcId)}/verify-profile`, {}),
    onSuccess: () => {
      setzeFreigabe(null)
      void queryClient.invalidateQueries({ queryKey: ['team', tenantId] })
    },
  })

  return (
    <div>
      <h4>Steuerprofile</h4>
      <p className="hinweis">
        Die Freigabe bestätigt, dass das Steuerprofil des Empfängers geprüft ist. Erst danach trägt eine
        Rechnung dieses Empfängers ihren Umsatzsteuer-Hinweis ohne Warnung.
      </p>
      {team.isPending && <Zustand art="laedt" beschriftung="Die Empfänger werden geladen." />}
      {team.data?.team.length === 0 && (
        <Zustand
          art="leer"
          titel="Für diesen Auftraggeber ist noch kein Empfänger hinterlegt."
          hinweis="Laden Sie oben einen Owner ein oder verknüpfen Sie einen bestehenden Auftragnehmer; danach steht sein Steuerprofil hier."
        />
      )}
      <ul className="kontrolle">
        {(team.data?.team ?? []).map((zeile) => (
          <li key={zeile.tenant_contractor_id}>
            {zeile.display_name} — {STEUERPROFIL_TEXT[zeile.vat_mode]}
            {zeile.verified_at === null
              ? (
                <>
                  {' — ungeprüft '}
                  {/* Der Knopf öffnet die Nachfrage, er löst nichts aus: die Freigabe gilt
                      plattformweit und lässt sich nirgends zurücknehmen. */}
                  <Knopf
                    variante="still"
                    disabled={freigeben.isPending}
                    onClick={() => { freigeben.reset(); setzeFreigabe(zeile) }}
                  >
                    Steuerprofil freigeben
                  </Knopf>
                </>
              )
              : ` — geprüft am ${datumZeit(zeile.verified_at)}`}
          </li>
        ))}
      </ul>
      {team.isError && <p role="alert" className="fehler">{fehlermeldung(team.error)}</p>}
      {freigeben.isError && freigabe === null && (
        <p role="alert" className="fehler">{fehlermeldung(freigeben.error)}</p>
      )}

      {freigabe === null ? null : (
        <SteuerprofilFreigabeDialog
          name={freigabe.display_name}
          profil={STEUERPROFIL_TEXT[freigabe.vat_mode]}
          laeuft={freigeben.isPending}
          fehler={freigeben.error === null ? null : fehlermeldung(freigeben.error)}
          onBestaetigen={() => { freigeben.mutate(freigabe.tenant_contractor_id) }}
          onSchliessen={() => { freigeben.reset(); setzeFreigabe(null) }}
        />
      )}
    </div>
  )
}
